import { useCallback } from 'react';
import { useSpace } from '../context/SpaceContext';
import { useAuth } from '../context/AuthContext';

// Types for workspace state (simplified for console logging)
interface MemoryWorkspaceState {
  view?: string;
  spaceId?: string;
  spaceName?: string;
  searchQuery?: string;
  activeFilter?: string;
  memoryCount?: number;
}

/**
 * Hook for tracking memory workspace state changes
 * Simplified implementation without external analytics dependencies
 */
export function useMemoryWorkspaceTracking() {
  const { currentSpace, availableSpaces } = useSpace();
  const { user } = useAuth();

  /**
   * Track memory workspace state change
   */
  const trackWorkspaceState = useCallback((state: MemoryWorkspaceState) => {
    console.log('🧠 Memory workspace state:', {
      view: state.view,
      space: state.spaceName || currentSpace?.name,
      searchQuery: state.searchQuery,
      memoryCount: state.memoryCount
    });
  }, [currentSpace]);

  /**
   * Track memory-related actions within the workspace
   */
  const trackMemoryAction = useCallback((action: string, memoryData?: Record<string, any>) => {
    console.log('🧠 Memory action:', action, {
      space_name: currentSpace?.name,
      user_tier: user?.user_metadata?.tier || 'free',
      ...memoryData
    });
  }, [currentSpace, user]);

  /**
   * Track space-related actions
   */
  const trackSpaceAction = useCallback((action: string, spaceData?: Record<string, any>) => {
    console.log('🗂️ Space action:', action, {
      current_space_name: currentSpace?.name,
      total_spaces: availableSpaces?.length || 0,
      user_tier: user?.user_metadata?.tier || 'free',
      ...spaceData
    });
  }, [currentSpace, availableSpaces, user]);

  return {
    trackWorkspaceState,
    trackMemoryAction,
    trackSpaceAction
  };
}

/**
 * Predefined workspace tracking functions for common actions
 */
export function useMemoryWorkspaceActions() {
  const { trackMemoryAction, trackSpaceAction, trackWorkspaceState } = useMemoryWorkspaceTracking();

  return {
    // Memory actions
    onMemoryCreated: (memory: { content_length: number; has_tags: boolean; creation_method?: string }) =>
      trackMemoryAction('created', memory),
    
    onMemoryViewed: (memoryId: string) =>
      trackMemoryAction('viewed', { memory_id: memoryId }),
    
    onMemoryEdited: (memoryId: string, changes: Record<string, any>) =>
      trackMemoryAction('edited', { memory_id: memoryId, ...changes }),
    
    onMemoryDeleted: (memoryId: string) =>
      trackMemoryAction('deleted', { memory_id: memoryId }),
    
    onMemorySearched: (query: string, resultCount: number) =>
      trackMemoryAction('searched', { 
        search_query: query, 
        result_count: resultCount,
        query_length: query.length
      }),

    // Space actions  
    onSpaceCreated: (space: { name: string; template_type: string }) =>
      trackSpaceAction('created', space),
    
    onSpaceChanged: (spaceId: string, spaceName: string) =>
      trackSpaceAction('changed', { 
        new_space_id: spaceId, 
        new_space_name: spaceName 
      }),
    
    onSpaceDeleted: (spaceId: string, spaceName: string) =>
      trackSpaceAction('deleted', { 
        deleted_space_id: spaceId, 
        deleted_space_name: spaceName 
      }),

    // Workspace view changes
    onViewMemoriesList: () =>
      trackWorkspaceState({ view: 'memories_list' }),
    
    onViewSearch: (query?: string) =>
      trackWorkspaceState({ 
        view: 'search', 
        searchQuery: query 
      }),
    
    onViewTags: (filter?: string) =>
      trackWorkspaceState({ 
        view: 'tags', 
        activeFilter: filter 
      }),
    
    onViewCreateMemory: () =>
      trackWorkspaceState({ view: 'create_memory' }),
    
    onViewSpaceManagement: () =>
      trackWorkspaceState({ view: 'space_management' }),

    // Feature discovery within workspace
    onFeatureDiscovered: (feature: string) =>
      trackMemoryAction('feature_discovered', { 
        feature, 
        discovery_context: 'memory_workspace' 
      })
  };
}